// ModalProtocolCreateEdit.jsx
import "./ModalProtocolCreateEdit.scss";
import "react-quill/dist/quill.snow.css";

import * as formik from "formik";
import * as yup from "yup";

import { Form, Modal } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppInput from "../../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import HallAppToast from "../../../../../commons/HallAppToast";
import ModalProtocolEditor from "./ModalProtocolEditor";
import ProtocolAPI from "../../api/ProtocolAPI";
import SelectBasicInput from "../../../../../commons/inputs/SelectBasicInput";

function toTagsArray(tagsText) {
  return (tagsText || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function stripHtml(html) {
  return (html || "").replace(/<[^>]*>/g, "").trim();
}

const protocolAPI = new ProtocolAPI();

function ModalProtocolCreateEdit({ show, closeShow, protocolToEdit, callProtocols }) {
  const { Formik } = formik;

  const [isLoading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // upload (front)
  const [pdfFile, setPdfFile] = useState(null);

  // quando estiver editando e já existir PDF
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);

  // modal fullscreen do editor
  const [showEditor, setShowEditor] = useState(false);

  const initialValues = useMemo(
    () => ({
      name: "",
      description: "",
      tagsText: "",
      mode: "PDF", // PDF | CUSTOM
      customHtml: "",
    }),
    []
  );

  const [formInitialValues, setFormInitialValues] = useState(initialValues);

  const schema = yup.object().shape({
    name: yup.string().required("Nome é obrigatório").max(80),
    description: yup.string().max(200),
    mode: yup.string().required("Selecione o tipo"),
    tagsText: yup.string().max(200),
    customHtml: yup.string(),
  });

  useEffect(() => {
    let alive = true;

    if (protocolToEdit && protocolToEdit.id) {
      setIsEdit(true);
      setExistingPdfUrl(protocolToEdit.documents);

      setFormInitialValues({
        name: protocolToEdit?.name || "",
        description: protocolToEdit?.description || "",
        tagsText: (protocolToEdit?.tags || []).join(", "),
        mode: protocolToEdit?.mode || "PDF",
        customHtml: protocolToEdit?.customHtml || "",
      });

    } else {
      setIsEdit(false);
      setFormInitialValues(initialValues);
      setPdfFile(null);
      setExistingPdfUrl(null);
    }

    return () => {
      alive = false;
    };
  }, [protocolToEdit, initialValues]);

  function onPickPdf(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf =
      (file.type || "").includes("pdf") || file.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      HallAppToast.warning("Selecione um arquivo PDF.");
      e.target.value = "";
      return;
    }

    setPdfFile(file);
  }

  const createOrUpdate = async (values, { resetForm }) => {
    try {
      setLoading(true);

      const hasExistingPdf = !!existingPdfUrl;

      // validações
      if (values.mode === "PDF" && !pdfFile && !hasExistingPdf) {
        HallAppToast.warning("Selecione um PDF para enviar.");
        return;
      }

      if (values.mode === "CUSTOM") {
        const text = stripHtml(values.customHtml);
        if (!text) {
          HallAppToast.warning("Abra o editor e preencha o conteúdo do protocolo.");
          return;
        }
      }

      const data = {
        name: values.name?.trim(),
        description: values.description?.trim(),
        mode: values.mode, // PDF | CUSTOM
        tags: toTagsArray(values.tagsText),
        customHtml: values.mode === "CUSTOM" ? values.customHtml : null,
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      if (values.mode === "PDF") {
        if (pdfFile) formData.append("files", pdfFile);
      }

      if (isEdit) {
        // ⚠️ ajuste o nome do método conforme seu ProtocolAPI
        // ex: updateMultipart(id, formData) ou update(id, formData)
        await protocolAPI.updateMultipart(protocolToEdit.id, formData);
      } else {
        await protocolAPI.createMultipart(formData);
      }

      HallAppToast.success(isEdit ? "Protocolo atualizado." : "Protocolo criado.");

      resetForm();
      setPdfFile(null);
      setExistingPdfUrl(null);

      callProtocols?.();
      closeShow();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={closeShow} centered className="proto-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            {isEdit ? "ATUALIZAR" : "ADICIONAR"} PROTOCOLO
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <AppContainer>
            <Formik
              validationSchema={schema}
              onSubmit={createOrUpdate}
              initialValues={formInitialValues}
              enableReinitialize
            >
              {({ handleSubmit, handleChange, values, touched, errors, setFieldValue }) => (
                <>
                  <Form noValidate onSubmit={handleSubmit} className="proto-form">
                    <SelectBasicInput
                      label="Tipo do protocolo"
                      name="mode"
                      value={values.mode}
                      onChange={(e) => {
                        handleChange(e);
                        const next = e.target.value;

                        if (next === "PDF") {
                          setFieldValue("customHtml", "");
                        } else {
                          setPdfFile(null);
                        }
                      }}
                      errors={errors}
                      touched={touched}
                      options={[
                        { key: "PDF", label: "PDF (upload)" },
                        { key: "CUSTOM", label: "Customizado (editor)" },
                      ]}
                    />

                    <AppInput
                      label="Nome do protocolo"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                    />

                    <AppInput
                      label="Descrição (opcional)"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: Indicação, objetivo, observações importantes..."
                    />

                    <AppInput
                      label="Tags (separadas por vírgula)"
                      name="tagsText"
                      value={values.tagsText}
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      placeholder="Ex: oleosidade, tricologia, homecare"
                    />

                    {/* PDF */}
                    {values.mode === "PDF" ? (
                      <div className="proto-upload">
                        <div className="proto-upload__label">Arquivo PDF</div>

                        <label className="proto-upload__pick">
                          Selecionar PDF
                          <input type="file" accept="application/pdf" onChange={onPickPdf} />
                        </label>

                        <div className="proto-upload__hint">
                          {pdfFile ? (
                            <>
                              Selecionado: <strong>{pdfFile.name}</strong>
                            </>
                          ) : existingPdfUrl ? (
                            <>PDF atual já cadastrado.</>
                          ) : (
                            "Nenhum PDF selecionado."
                          )}
                        </div>

                        {existingPdfUrl && !pdfFile ? (
                          <div className="proto-upload__hint" style={{ marginTop: 8 }}>
                            <AppLinkButton
                              name="ver pdf"
                              onClick={() =>
                                window.open(existingPdfUrl, "_blank", "noopener,noreferrer")
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {/* CUSTOM */}
                    {values.mode === "CUSTOM" ? (
                      <div className="proto-custom-card">
                        <div className="proto-custom-card__top">
                          <div>
                            <div className="proto-custom-card__title">Conteúdo do protocolo</div>
                            <div className="proto-custom-card__sub">
                              Edite em tela cheia para aproveitar melhor o espaço.
                            </div>
                          </div>

                          <AppButton
                            type="button"
                            width="auto"
                            name="editar"
                            onClick={() => setShowEditor(true)}
                          />
                        </div>

                        <br></br>
                        <div className="proto-custom-card__preview">
                          {stripHtml(values.customHtml) ? (
                            <div className="proto-custom-card__previewText">
                              {stripHtml(values.customHtml).slice(0, 50)}
                              {stripHtml(values.customHtml).length > 30 ? "..." : ""}
                            </div>
                          ) : (
                            <div className="proto-custom-card__empty">
                              Nenhum conteúdo ainda. Clique em <strong>Editar</strong>.
                            </div>
                          )}
                        </div>
                        <hr></hr>
                      </div>
                    ) : null}

                    <div className="proto-footer">
                      {isLoading ? (
                        <AppLoading />
                      ) : (
                        <AppButton
                          width="100%"
                          type="submit"
                          name={isEdit ? "atualizar" : "adicionar"}
                        />
                      )}

                      <div style={{ marginTop: 10 }}>
                        <AppLinkButton name="fechar" onClick={closeShow} />
                      </div>
                    </div>
                  </Form>

                  <ModalProtocolEditor
                    show={showEditor}
                    closeShow={() => setShowEditor(false)}
                    initialHtml={values.customHtml}
                    onApply={(html) => {
                      setFieldValue("customHtml", html);
                      HallAppToast.successWithMessage("Edição aplicada.");
                    }}
                  />
                </>
              )}
            </Formik>
          </AppContainer>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ModalProtocolCreateEdit;
